import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import useClient from 'hooks/useClient'
import { getProducts, getSupProducts, getProductSizes } from 'redux/actions/products'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CategoriesService from 'redux/middlewares/categories'
import Button from 'components/Button'
import SortableColumnTitle from 'components/SortableColumnTitle'
import PageTable from 'components/PageTable'
import { PERMISSIONS } from 'utils/constants'

const DEFAULT_PAGE_SIZE = 20

/** Matches `propertiesKeyMap` values / API sort fields */
const SORT_API_FIELDS = new Set(['title', 'arabianTitle', 'categoryName', 'shortName', 'currentPrice'])

const PAGE_SIZE_OPTIONS = new Set([10, 20, 50, 100])

function parseProductsViewParams(searchParams) {
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1)
  const rawSize = Number(searchParams.get('pageSize'))
  const pageSize = PAGE_SIZE_OPTIONS.has(rawSize) ? rawSize : DEFAULT_PAGE_SIZE

  const sort = searchParams.get('sort')
  const sortType = sort && SORT_API_FIELDS.has(sort) ? sort : null
  const order = searchParams.get('order')
  const sortOrder = sortType && (order === 'ascend' || order === 'descend') ? order : null

  const cid = searchParams.get('categoryId')
  let filterByCategoryId = null
  if (cid != null && cid !== '') {
    const n = Number(cid)
    if (Number.isFinite(n) && n > 0) filterByCategoryId = n
  }

  const vis = searchParams.get('visibility')
  const filterByVisibility = vis === 'true' ? true : vis === 'false' ? false : null
  const search = (searchParams.get('q') || '').trim()
  const rawProductSize = (searchParams.get('productSize') || '').trim()
  const filterByProductSizes = rawProductSize === ''
    ? []
    : rawProductSize
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0)

  return {
    currentPage: page,
    pageSize,
    sortType: sortOrder ? sortType : null,
    sortOrder: sortType ? sortOrder : null,
    filterByCategoryId,
    filterByVisibility,
    filterByProductSizes,
    search,
  }
}

function buildProductsSearchParams(state) {
  const {
    currentPage,
    pageSize,
    sortType,
    sortOrder,
    filterByCategoryId,
    filterByVisibility,
    filterByProductSizes,
    search,
  } = state
  const params = new URLSearchParams()
  if (currentPage !== 1) params.set('page', String(currentPage))
  if (pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pageSize))
  if (sortType && sortOrder) {
    params.set('sort', sortType)
    params.set('order', sortOrder)
  }
  if (filterByCategoryId != null) params.set('categoryId', String(filterByCategoryId))
  if (filterByVisibility !== null) params.set('visibility', String(filterByVisibility))
  if (Array.isArray(filterByProductSizes) && filterByProductSizes.length > 0) {
    params.set('productSize', filterByProductSizes.join(','))
  }
  if (search) params.set('q', search)
  return params
}

function paramsToStableString(params) {
  return [...new URLSearchParams(params).entries()]
    .sort(([aKey, aVal], [bKey, bVal]) => (aKey === bKey ? aVal.localeCompare(bVal) : aKey.localeCompare(bKey)))
    .map(([k, v]) => `${k}=${v}`)
    .join('&')
}

const Products = () => {
  const { t } = useTranslation()
  const { isClientCan } = useClient()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { items: productItems, pagination, isGetLoading, productSizes } = useSelector(
    (state) => state.products,
  )
  const [searchParams, setSearchParams] = useSearchParams()

  // Read URL params once on mount (refresh should restore state).
  const initialStateRef = useRef(null)
  if (initialStateRef.current == null) {
    initialStateRef.current = parseProductsViewParams(searchParams)
  }
  const initialState = initialStateRef.current

  const [pageSize, setPageSize] = useState(initialState.pageSize)
  const [currentPage, setCurrentPage] = useState(initialState.currentPage)
  const [sortType, setSortType] = useState(initialState.sortType)
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder)
  const [filterByCategoryId, setFilterByCategoryId] = useState(initialState.filterByCategoryId)
  const [filterByVisibility, setFilterByVisibility] = useState(initialState.filterByVisibility)
  const [filterByProductSizes, setFilterByProductSizes] = useState(
    initialState.filterByProductSizes,
  )
  const [search, setSearch] = useState(initialState.search)
  const [tableInstanceKey, setTableInstanceKey] = useState(0)

  useEffect(() => {
    const next = buildProductsSearchParams({
      currentPage,
      pageSize,
      sortType,
      sortOrder,
      filterByCategoryId,
      filterByVisibility,
      filterByProductSizes,
      search,
    })
    const nextStr = paramsToStableString(next)
    const currentStr = paramsToStableString(searchParams)
    if (nextStr !== currentStr) {
      setSearchParams(next, { replace: true })
    }
  }, [
    currentPage,
    pageSize,
    sortType,
    sortOrder,
    filterByCategoryId,
    filterByVisibility,
    filterByProductSizes,
    search,
    searchParams,
    setSearchParams,
  ])

  const isClientCanUpdateProducts = isClientCan(PERMISSIONS.UPDATE_PRODUCTS)

  useEffect(() => {
    dispatch(getProductSizes())
  }, [dispatch])

  useEffect(() => {
    dispatch(
      getProducts({
        page: currentPage,
        size: pageSize,
        sortType,
        sortOrder,
        filterByCategoryId,
        filterByVisibility,
        search,
        isSubscriptionVisible: true,
        filterByProductSizes,
      }),
    )
  }, [
    dispatch,
    currentPage,
    pageSize,
    sortType,
    sortOrder,
    filterByCategoryId,
    filterByVisibility,
    filterByProductSizes,
    search,
  ])

  const { data: allCategories } = useQuery(['allProductsCategories'], async () => {
    const response = await CategoriesService.getCategories(undefined, undefined, true)
    return response.data.data
  })

  const refreshSupProducts = () => dispatch(getSupProducts())

  const categoriesFilerOptions = useMemo(() => {
    return (allCategories?.result || []).map((oneCategory) => ({
      text: oneCategory.name,
      value: oneCategory.id,
    }))
  }, [allCategories])

  const productSizeFilterOptions = useMemo(() => {
    return (productSizes || []).map((size) => ({
      text: size,
      value: size,
    }))
  }, [productSizes])

  const propertiesKeyMap = {
    products_col_Title: 'title',
    products_col_arabianTitle: 'arabianTitle',
    products_col_categoryName: 'categoryName',
    products_col_shortName: 'shortName',
    products_col_priceForOne: 'currentPrice',
  }

  const tableChangeHandler = (pagination, filter, sorter) => {
    setCurrentPage(pageSize === pagination.pageSize ? pagination.current : 1)
    setPageSize(pagination.pageSize)
    setSortType(propertiesKeyMap[sorter.columnKey] || null)
    setSortOrder(sorter.order)
    setFilterByCategoryId(
      filter?.products_col_categoryName ? filter.products_col_categoryName[0] : null,
    )
    const visibilityValue = filter?.isVisible?.[0]
    setFilterByVisibility(
      visibilityValue === true || visibilityValue === 'true'
        ? true
        : visibilityValue === false || visibilityValue === 'false'
          ? false
          : null,
    )
    setFilterByProductSizes(
      Array.isArray(filter?.productSize) && filter.productSize.length > 0
        ? filter.productSize.map((v) => String(v))
        : [],
    )
  }

  const hasActiveFilters =
    currentPage !== 1 ||
    pageSize !== DEFAULT_PAGE_SIZE ||
    sortType != null ||
    filterByCategoryId != null ||
    filterByVisibility != null ||
    (Array.isArray(filterByProductSizes) && filterByProductSizes.length > 0) ||
    search !== ''

  const resetFilters = useCallback(() => {
    setCurrentPage(1)
    setPageSize(DEFAULT_PAGE_SIZE)
    setSortType(null)
    setSortOrder(null)
    setFilterByCategoryId(null)
    setFilterByVisibility(null)
    setFilterByProductSizes([])
    setSearch('')
    setTableInstanceKey((k) => k + 1)
  }, [])

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 57,
    },
    {
      title: (props) => (
        <SortableColumnTitle {...props} title={t('fields.name en')} id="products_col_Title" />
      ),
      dataIndex: 'title',
      key: 'products_col_Title',
      width: 257,
      sorter: true,
      sortOrder: sortType === 'title' ? sortOrder : null,
    },
    {
      title: (props) => (
        <SortableColumnTitle
          {...props}
          title={t('fields.name ar')}
          id="products_col_arabianTitle"
        />
      ),
      render: (text) => <p dir="rtl">{text}</p>,
      dataIndex: 'arabianTitle',
      key: 'products_col_arabianTitle',
      width: 221,
      sorter: true,
      align: 'right',
      sortOrder: sortType === 'arabianTitle' ? sortOrder : null,
    },
    {
      title: t('fields.product category'),
      dataIndex: ['category', 'name'],
      key: 'products_col_categoryName',
      width: 160,
      filters: categoriesFilerOptions,
      filterMultiple: false,
      filteredValue: filterByCategoryId != null ? [filterByCategoryId] : null,
    },
    {
      title: t('fields.sap id'),
      dataIndex: 'sapId',
      key: 'sapId',
      width: 84,
    },
    {
      title: t('fields.product size', 'Product size'),
      dataIndex: 'productSize',
      key: 'productSize',
      width: 130,
      filters: productSizeFilterOptions,
      filterMultiple: true,
      filterSearch: true,
      filteredValue:
        Array.isArray(filterByProductSizes) && filterByProductSizes.length > 0
          ? filterByProductSizes
          : null,
      render: (productSize) => productSize || '-',
    },
    {
      title: (props) => (
        <SortableColumnTitle {...props} title={t('fields.pack')} id="products_col_shortName" />
      ),
      dataIndex: 'shortName',
      key: 'products_col_shortName',
      width: 129,
      sorter: true,
      sortOrder: sortType === 'shortName' ? sortOrder : null,
    },
    {
      title: (props) => (
        <SortableColumnTitle
          {...props}
          title={t('fields.price for 1')}
          id="products_col_priceForOne"
        />
      ),
      dataIndex: 'currentPrice',
      key: 'products_col_priceForOne',
      width: 115,
      sorter: true,
      sortOrder: sortType === 'currentPrice' ? sortOrder : null,
    },
    {
      title: t('fields.visibility'),
      dataIndex: 'isVisible',
      key: 'isVisible',
      width: 114,
      filters: [
        { text: t('app.active'), value: true },
        { text: t('app.disable'), value: false },
      ],
      filterMultiple: false,
      filteredValue: filterByVisibility != null ? [filterByVisibility] : null,
      render: (isVisible) => (isVisible ? t('app.active') : t('app.disable')),
    },
  ]

  return (
    <PageTable
      key={tableInstanceKey}
      pageTitle={t('products.title')}
      action={
        <>
          <Button type="default" disabled={!hasActiveFilters} onClick={resetFilters}>
            <span className="btn-text">{t('app.reset')}</span>
          </Button>
          {isClientCanUpdateProducts ? (
            <Button type="primary" onClick={refreshSupProducts}>
              <span className="btn-text">{t('promotions.pull')}</span>
            </Button>
          ) : null}
        </>
      }
      defaultSearchValue={search}
      baseData={productItems || []}
      columns={columns}
      onRow={
        isClientCanUpdateProducts
          ? (record) => ({
              onClick: () => navigate(`/main/products/view/${record.id}`),
            })
          : null
      }
      loading={isGetLoading}
      pagination={{
        pageSize,
        current: currentPage,
        total: pagination?.total,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: (pagination?.total ?? 0) >= 10 || true,
      }}
      onChange={tableChangeHandler}
      searchHandler={(value) => {
        const nextSearch = value.trim()
        setSearch(nextSearch)
        if (currentPage !== 1) setCurrentPage(1)
      }}
    />
  )
}

export default Products
